import React from 'react';

import { useForm, useFieldArray } from 'react-hook-form';
import Campo from './Campo';
import { FaAngleDoubleUp } from 'react-icons/fa';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import { Alert } from '../Alert';

const MontaFormulario = ({ id, campos, campos_defaultValues = {}, jaEnviado = false, podeReenviar = false, pareceres, onSubmit = () => ({}), onReenviarInformacoes = () => ({}) }) => {
    const funcaoWhen = (campos, regras) => {
        let retStrFuncaoWhen = `
        (${campos.join(',')}, schema) => {        
            ${regras.join('\n')} 
            return schema;
        }`;
        return retStrFuncaoWhen;
    };

    const montaRegraCampoObrigatoriedade = campo => {
        let regra_yup = {};
        let campos_envolvidos = [];
        let regrasWhen = [];

        if (Object.keys(campo.regras_obrigatoriedade).length === 0) return regra_yup;

        if (campo.tipo === 'checkbox' || campo.tipo === 'upload') regra_yup[campo.nome] = yup.array();
        else regra_yup[campo.nome] = yup.string();

        campo.regras_obrigatoriedade.forEach(regra => {
            if (typeof regra === 'boolean') {
                regra_yup[campo.nome] = regra_yup[campo.nome].required();
            } else {
                let regras_and = [];
                for (let [regraCampo, regraValores] of Object.entries(regra)) {
                    regraValores.forEach(valor => {
                        regras_and.push(`${regraCampo}?.includes && ${regraCampo}.includes('${valor}')`);
                    });
                    if (!campos_envolvidos.includes(regraCampo)) campos_envolvidos.push(regraCampo);
                }
                regrasWhen.push(`if(${regras_and.join(' && ')}) return schema.required();`);
            }
        });

        if (regrasWhen.length > 0) {
            // eslint-disable-next-line no-eval
            regra_yup[campo.nome] = regra_yup[campo.nome].when(campos_envolvidos, eval(funcaoWhen(campos_envolvidos, regrasWhen)));
        }

        return regra_yup;
    };

    const montaRegraObrigatoriedade = () => {
        let regra_yup = {};

        campos.forEach(campo => {
            if (campo.replicador) {
                let regras_replicador = {};
                campo.campos.forEach(campo_replicador => {
                    if (campo_replicador?.regras_obrigatoriedade) {
                        regras_replicador = { ...regras_replicador, ...montaRegraCampoObrigatoriedade(campo_replicador) };
                    }
                });

                /* nenhum campo com regra e o replicador não obrigatório */
                if (Object.keys(regras_replicador).length === 0 && !campo.obrigatorio) return;

                regra_yup[campo.nome] = yup.array();

                if (Object.keys(regras_replicador).length) regra_yup[campo.nome] = regra_yup[campo.nome].of(yup.object().shape(regras_replicador));

                /* Define o replicador como obrigatório, porém além dessa validação foi garantido que ele sempre tenha 1 item e que este não possa ser removido*/
                if (campo.obrigatorio) regra_yup[campo.nome] = regra_yup[campo.nome].required();
            } else {
                if (campo?.regras_obrigatoriedade) regra_yup = { ...regra_yup, ...montaRegraCampoObrigatoriedade(campo) };
            }
        });

        /* Nenhum campo obrigatório então não terá esquema de validação */
        if (Object.keys(regra_yup).length === 0) return null;

        return yup.object().shape(regra_yup);
    };

    const { control, register, unregister, handleSubmit, errors, watch, getValues } = useForm({
        defaultValues: campos_defaultValues,
        resolver: yupResolver(montaRegraObrigatoriedade()),
        mode: 'onSubmit',
    });

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            {campos.map((campo, index) => {
                campo.desabilitado = jaEnviado || !podeReenviar;
                return <MontaCampo key={index} campo={campo} propsForm={{ control, register, unregister, errors, watch, getValues }} pareceres={pareceres} />;
            })}
            {!jaEnviado && podeReenviar && (
                <>
                    <small className="form-text text-muted text-left">* Preenchimento obrigatório</small>
                    <div className="card-footer text-center">
                        <button type="submit" className="btn btn-primary">
                            Confirmar envio
                        </button>
                    </div>
                </>
            )}
            {jaEnviado && podeReenviar && (
                <>
                    <p className="mt-3">Caso você queira alterar os documentos enviados e gerar um novo protocolo, pressione o botão "Reenviar documentos" abaixo.</p>
                    <hr />
                    <button type="button" className="btn btn-primary" data-toggle="modal" data-target={`#reenviar_modal_${id}`}>
                        Reenviar documentos
                    </button>
                    <div className="modal fade" id={`reenviar_modal_${id}`} tabIndex="-1" role="dialog">
                        <div className="modal-dialog" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Atenção</h5>
                                    <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <p>Ao reenviar novos outros documentos, os enviados anteriormente serão desconsiderados</p>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-outline-secondary order-1 order-sm-0" data-dismiss="modal">
                                        Cancelar
                                    </button>
                                    <button type="button" className="btn btn-primary order-0 order-sm-1" data-dismiss="modal" onClick={() => onReenviarInformacoes()}>
                                        Continuar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </form>
    );
};

const MontaCampo = ({ campo, propsForm, pareceres }) => {
    const campoNome = campo.nome;
    const { fields, append, remove } = useFieldArray({
        control: propsForm.control,
        name: campoNome,
    });

    const confirmaRemocaoLinhaReplicador = (campo, linha) => (
        <>
            <button type="button" className="btn btn-primary btn-outline-success" data-toggle="modal" data-target={`#remover_${campo.nome}_${linha}`}>
                Remover
            </button>
            <div className="modal fade" id={`remover_${campo.nome}_${linha}`} tabIndex="-1" role="dialog">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Atenção</h5>
                            <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>Confirma a remoção do item?</p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-outline-secondary order-1 order-sm-0" data-dismiss="modal">
                                Cancelar
                            </button>
                            <button type="button" className="btn btn-primary order-0 order-sm-1" data-dismiss="modal" onClick={() => remove(linha)}>
                                Continuar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    if (campo.replicador) {
        if (0 === fields.length && campo.obrigatorio) {
            append({}, false);
        }
        return (
            <>
                <div className="form-replicador" id={`replicador_${campo.nome}`}>
                    {fields.map((linha, indiceLinha) => {
                        return (
                            <div key={linha.id} className="card">
                                {pareceres && pareceres[campo.nome] && pareceres[campo.nome][indiceLinha] && pareceres[campo.nome][indiceLinha].documentacao_aceita === 'N' ? (
                                    <div
                                        className="card-header--alert accordion accordion--alert"
                                        id={`header_${campo.nome}_${indiceLinha}`}
                                        data-toggle="collapse"
                                        data-target={`#${campo.nome}_${indiceLinha}`}
                                        aria-expanded="true"
                                        aria-controls="collapse"
                                    >
                                        <Alert variant="danger" icon={false}>
                                            {campo.label} {indiceLinha + 1} <br />
                                            <b>Avaliação: </b> Documentação indeferida pela comissão avaliadora <br />
                                            <b>Parecer: </b> {pareceres[campo.nome][indiceLinha].documentacao_parecer}
                                            <FaAngleDoubleUp style={{ position: 'absolute', bottom: 10, right: 10 }} />
                                        </Alert>
                                    </div>
                                ) : (
                                    <div className="card-header" id={`header_${campo.nome}_${indiceLinha}`}>
                                        <h2 className="mb-0 d-flex">
                                            <button
                                                className="btn btn-link btn-block text-left card-title"
                                                type="button"
                                                data-toggle="collapse"
                                                data-target={`#${campo.nome}_${indiceLinha}`}
                                                aria-expanded="false"
                                                aria-controls={`${campo.nome}_${indiceLinha}`}
                                            >
                                                {campo.label} {indiceLinha + 1}
                                                <FaAngleDoubleUp style={{ position: 'absolute', bottom: 10, right: 10 }} />
                                            </button>
                                        </h2>
                                    </div>
                                )}

                                <div id={`${campo.nome}_${indiceLinha}`} className="collapse show" aria-labelledby="heading1" data-parent={`#replicador_${campo.nome}`}>
                                    <div className="card-body">
                                        {!campo.desabilitado && !campo.obrigatorio && confirmaRemocaoLinhaReplicador(campo, indiceLinha)}
                                        {!campo.desabilitado && campo.obrigatorio && fields.length > 1 && confirmaRemocaoLinhaReplicador(campo, indiceLinha)}

                                        <input type="hidden" ref={propsForm.register()} name={`${campo.nome}[${indiceLinha}].fake`} defaultValue="" />
                                        {campo.campos.map((campoDoGrupo, index) => {
                                            return <Campo key={index} campoPai={campo} indiceLinha={indiceLinha} campo={campoDoGrupo} propsForm={propsForm} remove={remove} />;
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {!campo.desabilitado && (
                    <button
                        type="button"
                        className="btn btn-primary btn-outline-success"
                        onClick={() => {
                            append();
                        }}
                    >
                        Adicionar outro(a) {campo.label}
                    </button>
                )}
            </>
        );
    }
    return <Campo campo={campo} propsForm={propsForm} remove={remove} />;
};

export default MontaFormulario;
